import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { Replay } from '@sentry/replay'
import { browserTracingIntegration } from '@sentry/vue'

// configs
import configs from '@/config'

const replay = new Replay({ maskAllText: false })

const init = () => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.init({
      release: configs.sentryRelease,
      dsn: configs.sentry,
      environment: configs.env,
      integrations: [
        browserTracingIntegration(),
        replay,
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.2,
      tracesSampleRate: 0.02,
      Vue,
      tracingOptions: {
        trackComponents: true,
      },
      ignoreErrors: [
        'The operation is insecure.',
        'fullscreen error',
        'null is not an object (evaluating \'a.error\')',
        'UET is not defined',
        'too much recursion',
        'Fullscreen request denied',
        'Maximum call stack size exceeded',
        'Unable to decode audio data',
        'Extension context invalidated.',
        'xhr poll error',
        'Cannot read property \'getReadModeConfig\' of undefined',
        '"Timeout"',
        'UnhandledRejection: Non-Error promise rejection captured with value: Timeout',
        'AbortError: The play() request was interrupted by a call to pause().',
        'can\'t access dead object',
        'this.editor is undefined',
        'Cannot read properties of undefined (reading \'setMode\')',
        'undefined is not an object (evaluating \'this.editor.setMode\')',
      ],
      denyUrls: [
        /analytics\.tiktok\.com/i,
        /js\.zohocdn\.com/i,
        /salesiq\.zoho\.com/i,
        /www\.gstatic\.com/i,
        /googletagmanager\.com/i,
        /\/gtm\.js(\?|$)/i,
        /\/gtag\/js(\?|$)/i,
      ],
      // beforeSend helps filtering which issues can go into Sentry
      // filter out all the responses with status equal to 0 (timeout request)
      beforeSend(event, hint) {
        const { originalException: error } = hint

        if (error) {
          if (error.status === 0) return null

          if (error.status >= 400 && error.status < 500) return null
        }

        if (configs.env !== 'production') {
          // log error to browser console
          // for better visibility into exceptions
          if (event?.extra?.error) {
            // eslint-disable-next-line no-console
            console.error(event.extra.error)
          } else if (error) {
            // eslint-disable-next-line no-console
            console.error(error)
          }
        }

        return event
      },
      enableLogs: true,
    })
  } catch {
    // do nothing
  }
}

const identifyUser = (currentUser) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.setUser(currentUser)
  } catch {
    // do nothing
  }
}

const captureMessage = (message) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureMessage(message)
  } catch {
    // do nothing
  }
}

const captureException = (error) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureException(error)
  } catch {
    // do nothing
  }
}

const startSessionReplay = async () => {
  if (!configs.sentryEnabled) return

  try {
    await replay.flush()
  } catch (error) {
    captureException(error)
  }
}

const stopSessionReplay = async () => {
  if (!configs.sentryEnabled) return

  try {
    await replay.stop()
  } catch (error) {
    captureException(error)
  }
}

export default {
  init,
  identifyUser,
  captureMessage,
  captureException,
  startSessionReplay,
  stopSessionReplay,
}
